import { useEffect, useRef } from 'react';
import Input from '../../../components/forms/input.component';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Button from '../../../components/forms/button.component';
import { AlertService } from '../../../services/alert.service';
import { AccountsService } from '../../../services/accounts.service';
import Icon from '../../../components/forms/icon.component';
import useModal from '../../../hooks/use-close-modal.hook';
import LogData from '../../../components/logs/log-data.component';
import { LocalStorageService } from '../../../services/local-storage.service';
import { STORAGE } from '../../../constants';
import useInputValidation from '../../../hooks/use-input-validation.hook';

interface EditPriceModalProps {
  show: boolean;
  onRequestClose: (refresh: boolean) => void;
  payload: {
    channelType: string;
    configId: string;
    currentPrice: number;
    configName: string;
    accountId: string;
    accountName: string;
    companyName: string;
    existingChannels: Array<{
      config: string;
      price: number;
    }>;
  };
}

function EditPriceModal({ show, onRequestClose, payload }: EditPriceModalProps) {
  const { t } = useTranslation();
  const modalRef = useRef<HTMLDivElement>(null);
  const username = LocalStorageService.get(STORAGE.SESSION)?.username;
  const { priceRegex } = useInputValidation();

  const getChannelDisplayName = (channelType: string) => {
    const channelNames: { [key: string]: string } = {
      whatsapp: 'WhatsApp',
      sms: 'SMS',
      email: 'Email',
      rcs_conversational: 'RCS Conversacional',
      rcs_single: 'RCS Single',
      rcs_basic: 'RCS Basic'
    };
    return channelNames[channelType] || channelType;
  };

  const getConfigLabel = (channelType: string) => {
    if (channelType === 'sms') return t('general.maskCapital');
    if (channelType === 'email') return 'Email';
    if (channelType.startsWith('rcs')) return t('general.agentCapital');
    return t('general.accountCapital');
  };

  useModal(modalRef, show);

  const defaultValues = {
    price: 0
  };

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isValid, isSubmitting }
  } = useForm({
    mode: 'onChange',
    defaultValues
  });

  const editPrice = async (data: any) => {
    const price = parseFloat(data.price);
    const existingChannels = payload.existingChannels || [];
    const updatedChannels = [...existingChannels];

    const configIndex = updatedChannels.findIndex((ch) => ch.config === payload.configId);

    if (configIndex !== -1) {
      updatedChannels[configIndex] = {
        ...updatedChannels[configIndex],
        price: price
      };
    } else {
      updatedChannels.push({
        config: payload.configId,
        price: price
      });
    }

    const requestBody = {
      account: payload.accountId,
      username: username,
      [payload.channelType]: updatedChannels
    };

    if (payload.channelType.startsWith('rcs')) {
      const rcsType = payload.channelType.split('_')[1];
      requestBody.type = rcsType;
      requestBody.rcs = requestBody[payload.channelType];
      delete requestBody[payload.channelType];
    }

    const buttonId = `editPrice-${payload.configId}`;

    await AlertService.warning(
      `<h2>${t('account-page.modal-edit-price.edit-price-title')}</h2>`,
      `<div class="text-gray-700 fs-6">
        <p class="mb-2">${t('account-page.modal-edit-price.edit-price-text')}</p>
        <ul class="list-unstyled">
          <li class="my-2"><strong>${t('general.accountCapital')}:</strong> ${payload.accountName}</li>
          <li class="my-2"><strong>${t('channels.channel')}:</strong> ${getChannelDisplayName(payload.channelType)}</li>
          <li class="my-2"><strong>${getConfigLabel(payload.channelType)}:</strong> ${payload.configName}</li>
          <li class="my-2 text-center"><strong>${t('general.price')}:</strong> $${data.price}</li>
        </ul>
      </div>`,
      async () => {
        try {
          if (payload.channelType === 'whatsapp') {
            await AccountsService.updateWAChannel(requestBody);
          } else if (payload.channelType === 'sms') {
            await AccountsService.updateSMSChannel(requestBody);
          } else if (payload.channelType === 'email') {
            await AccountsService.updateMailingChannel(requestBody);
          } else if (payload.channelType.startsWith('rcs')) {
            await AccountsService.updateRCSChannel(requestBody);
          }

          LogData(requestBody, buttonId, window.location.href);
          AlertService.toastSuccess(t('account-page.alert-success-edit-price'));
          onRequestClose(true);
          reset(defaultValues);
        } catch (errorCode: any) {
          console.log('Error: ', errorCode);
          AlertService.toastError(errorCode.error || errorCode);
        }
      },
      t('general.btn-add-modal')
    );
  };

  useEffect(() => {
    reset({
      price: payload?.currentPrice || defaultValues.price
    });
  }, [payload]);

  return (
    <>
      <div aria-hidden="true" className="modal fade" ref={modalRef} data-bs-backdrop="static">
        <div className="modal-dialog modal-mw-700px modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header justify-content-end border-0 pb-0">
              <div
                className="btn btn-sm btn-icon btn-active-light-primary"
                onClick={() => onRequestClose(false)}
              >
                <Icon icon="ki-cross" size="fs-1" />
              </div>
            </div>
            <div className="modal-body pt-0 pb-0 px-5 px-xl-20">
              <div className="mt-1 text-center">
                <h2>
                  {' '}
                  {t('account-page.modal-edit-price.title-edit-price')}{' '}
                  {getChannelDisplayName(payload?.channelType)}
                </h2>
                <div className="text-primary fw-bolder fs-5">
                  <span>{payload?.configName}</span>
                </div>
                <div className="separator border-2 my-5"></div>
              </div>
              <form onSubmit={handleSubmit(editPrice)}>
                <div className="fv-row mb-5">
                  <div className="row row-cols-1">
                    <div className="col">
                      <div className="fv-row mb-7">
                        <Input
                          label={`${t('general.price')}`}
                          requiredIndicator="required"
                          placeholder="0.00"
                          type="number"
                          step="0.01"
                          errors={errors.price}
                          {...register('price', {
                            required: true,
                            pattern: {
                              value: priceRegex,
                              message: t('forms.invalid-FeedBack.valid-price')
                            },
                            min: {
                              value: 0.01,
                              message: t('forms.invalid-FeedBack.min-price')
                            }
                          })}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-light hover-scale"
                    onClick={() => onRequestClose(false)}
                  >
                    {t('general.close')}
                  </button>
                  <Button
                    type="submit"
                    id="sendData"
                    className="btn btn-primary ms-3 hover-scale"
                    disabled={!isValid}
                    isLoading={isSubmitting}
                  >
                    <Icon icon="ki-send" size="fs-1" type="" />
                    {t('general.edit')} {t('general.price')}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default EditPriceModal;
